'use client'

import { ReactNode, useState } from 'react'

import { cn } from '@/shared/lib'
import { Icon, TextButton } from '@/shared/ui'

import * as Card from './card'

type CommonProps = {
  children: ReactNode
  className?: string
}

type ContractListProps = CommonProps & {
  contractListLength: number
  expandable?: boolean
}

export const Root = ({ children, className }: CommonProps) => {
  return (
    <Card.Root className={className} spacing="small">
      {children}
    </Card.Root>
  )
}

export const Header = ({ children, className }: CommonProps) => {
  return (
    <div className={cn('flex items-center justify-between', className)}>
      {children}
    </div>
  )
}

export const Status = ({ children, className }: CommonProps) => {
  return (
    <Card.ItemTitle className={cn('w-fit', className)}>
      {children}
    </Card.ItemTitle>
  )
}

export const Content = ({ children, className }: CommonProps) => {
  return (
    <div className={cn('mt-4 flex items-center gap-6', className)}>
      {children}
    </div>
  )
}

export const RoomInfo = ({ children, className }: CommonProps) => {
  return <div className={cn('flex-1', className)}>{children}</div>
}

export const RoomName = ({ children, className }: CommonProps) => {
  return (
    <h4
      className={cn('typo-headline4 mb-2 font-semibold text-black', className)}
    >
      {children}
    </h4>
  )
}

export const RoomButtons = ({ children, className }: CommonProps) => {
  return <div className={cn('mt-3 flex gap-1.5', className)}>{children}</div>
}

export const Actions = ({ children, className }: CommonProps) => {
  return (
    <div className={cn('flex-[0_0_160px] self-start text-right', className)}>
      {children}
    </div>
  )
}

const INITIAL_DISPLAY_COUNT = 3
const CONTRACT_HEIGHT = 104
const CONTRACT_GAP = 8

export const ContractList = ({
  children,
  className,
  contractListLength,
  expandable = true,
}: ContractListProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const showExpandButton =
    expandable && contractListLength > INITIAL_DISPLAY_COUNT

  const displayCount = isExpanded
    ? contractListLength
    : Math.min(INITIAL_DISPLAY_COUNT, contractListLength)

  const totalHeight =
    contractListLength > 0
      ? CONTRACT_HEIGHT * contractListLength +
        CONTRACT_GAP * (contractListLength - 1)
      : 0
  const displayHeight =
    displayCount > 0
      ? CONTRACT_HEIGHT * displayCount + CONTRACT_GAP * (displayCount - 1)
      : 0

  const handleToggleExpand = () => {
    setIsExpanded(prev => !prev)
  }

  return (
    <>
      <div
        className={cn(
          'mt-5 space-y-2 overflow-hidden transition-[height] duration-300 ease-in-out',
          className,
        )}
        style={{
          height:
            expandable && !isExpanded
              ? `${displayHeight}px`
              : `${totalHeight}px`,
        }}
      >
        {children}
      </div>
      {showExpandButton && (
        <div className="mt-6 flex justify-center">
          <TextButton onClick={handleToggleExpand} color="gray" size="small">
            {isExpanded ? '접기' : '더보기'}
            <Icon
              name="arrow-bottom"
              size={16}
              className={cn(
                'gap-0 transition-transform duration-300',
                isExpanded && 'rotate-180',
              )}
            />
          </TextButton>
        </div>
      )}
    </>
  )
}
